// Outline, geometry and profile validation (CIN-15126, phase 0). Pure. Returns the FIRST error found, or null.
// The client is the authority (the server checks structure only, see the plan, section 1.3).
//
// DECISIONS
// - Check order: structure (count, ids, integers, ranges, bulge, edge lengths) -> self-intersection ->
//   orientation -> screen edge -> area and size. Orientation comes before the screen edge because a
//   clockwise outline would otherwise report a misleading screen-edge error; self-intersection comes
//   first because a bow-tie has no meaningful orientation.
// - Codes beyond the plan's list (see OutlineError): `bad-vertex`, `not-ccw`, `level-front-edge`, `bad-profile`.
// - Range failures: |x| > 60,000 or y < -100,000 = `too-large`; y > 0 = `beyond-screen-wall`.
// - Self-intersection runs on a 50 mm tessellation over every pair of non-consecutive segments; consecutive
//   segments fail only when they fold back onto each other.
// - Size limits mirror ROOM_MIN/ROOM_MAX in room-studio collab/version-document.ts (1,000 / 200,000 mm),
//   duplicated on purpose: the ui package must not import room-studio.
// - Levels must lie inside the room outline: every tessellated point inside (5 mm slack) and every segment
//   midpoint inside, which catches an L notch cutting a level's edge. With no explicit outline the implicit
//   rectangle needs `room.width`/`room.depth`; without them containment is skipped.
// - Level footprints are validated like outlines but need no screen edge, at most 48 vertices, and an area
//   of at least 5 m2 (outlines: 10 m2).
// - Profiles: points are integers within +-60,000 mm, segments at least 50 mm, at most 48 points; closed
//   paths must be simple; steps <= 12 with tread 50..1000; openings <= 8 wide 300..10,000; thickness 10..2000;
//   a counter's cupholderSpacing 300..3000; `color` a #rrggbb hex, `texture` one of OBJECT_TEXTURES, a screen's
//   bezel 20..1500 wide and 10..600 deep (CIN-15200).

import { tessellateEdge } from './arc.ts';
import {
    edgesOf,
    pointInOutline,
    rectangleOutline,
    segmentsIntersect,
    signedArea,
    symmetricSize,
    tessellate
} from './outline.ts';
import { OBJECT_TEXTURES } from './types.ts';
import type {
    ClosedPath,
    ObjectProfile,
    OutlineError,
    OutlineLike,
    PlanPoint,
    RoomGeometry,
    RoomOutline
} from './types.ts';

export const OUTLINE_LIMITS = {
    minVertices: 3,
    maxVertices: 64,
    maxLevelVertices: 48,
    maxProfilePoints: 48,
    maxAbsX: 60_000,
    minY: -100_000,
    minChord: 300,
    minProfileChord: 50,
    minArea: 10_000_000,
    minLevelArea: 5_000_000,
    minScreenEdge: 2_000,
    roomMin: 1_000,
    roomMax: 200_000,
    maxLevels: 3,
    minElevation: 1_500,
    headroom: 2_200,
    minSlab: 100,
    maxSlab: 1_000,
    maxIdLength: 16,
    validationTol: 50
} as const;

const isInt = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n);
const validBulge = (b: unknown): boolean => b === undefined || (typeof b === 'number' && Math.abs(b) <= 1);

/** Structure of a closed path shared by outlines and level footprints; null when fine. */
function validatePath(path: ClosedPath, maxVertices: number): OutlineError | null {
    const { vertices } = path;
    if (!Array.isArray(vertices) || vertices.length < OUTLINE_LIMITS.minVertices) return 'too-few';
    if (vertices.length > maxVertices) return 'too-many';
    const ids = new Set<string>();
    for (const v of vertices) {
        if (typeof v.id !== 'string' || v.id === '' || v.id.length > OUTLINE_LIMITS.maxIdLength || ids.has(v.id))
            return 'bad-vertex';
        ids.add(v.id);
        if (!isInt(v.x) || !isInt(v.y)) return 'bad-vertex';
        if (Math.abs(v.x) > OUTLINE_LIMITS.maxAbsX || v.y < OUTLINE_LIMITS.minY) return 'too-large';
        if (v.y > 0) return 'beyond-screen-wall';
        if (!validBulge(v.bulge)) return 'bulge-range';
    }
    if (edgesOf(path).some((e) => e.chord < OUTLINE_LIMITS.minChord)) return 'short-edge';
    return null;
}

const cross = (a: PlanPoint, b: PlanPoint): number => a.x * b.y - a.y * b.x;

/** Whether a closed or open polyline crosses or touches itself; consecutive segments only when folded back. */
function selfIntersects(points: PlanPoint[], closed: boolean): boolean {
    const n = points.length;
    const count = closed ? n : n - 1;
    const segment = (i: number): [PlanPoint, PlanPoint] => [points[i], points[(i + 1) % n]];
    const foldsBack = (first: [PlanPoint, PlanPoint], second: [PlanPoint, PlanPoint]): boolean => {
        const u = { x: first[1].x - first[0].x, y: first[1].y - first[0].y };
        const v = { x: second[1].x - second[0].x, y: second[1].y - second[0].y };
        const scale = Math.hypot(u.x, u.y) * Math.hypot(v.x, v.y);
        return Math.abs(cross(u, v)) < 1e-9 * scale && u.x * v.x + u.y * v.y < 0;
    };
    for (let i = 0; i < count; i += 1) {
        const [a, b] = segment(i);
        for (let j = i + 1; j < count; j += 1) {
            const [c, d] = segment(j);
            if (j === i + 1) {
                if (foldsBack([a, b], [c, d])) return true;
                continue;
            }
            if (closed && i === 0 && j === count - 1) {
                if (foldsBack([c, d], [a, b])) return true;
                continue;
            }
            if (Math.max(a.x, b.x) < Math.min(c.x, d.x) || Math.max(c.x, d.x) < Math.min(a.x, b.x)) continue;
            if (Math.max(a.y, b.y) < Math.min(c.y, d.y) || Math.max(c.y, d.y) < Math.min(a.y, b.y)) continue;
            if (segmentsIntersect(a, b, c, d)) return true;
        }
    }
    return false;
}

export function validateOutline(o: RoomOutline): OutlineError | null {
    const structural = validatePath(o, OUTLINE_LIMITS.maxVertices);
    if (structural) return structural;
    if (selfIntersects(tessellate(o, OUTLINE_LIMITS.validationTol).points, true)) return 'self-intersect';
    const area = signedArea(o);
    if (area <= 0) return 'not-ccw';
    const screen = edgesOf(o).find((e) => e.isScreen);
    if (!screen) return 'no-screen-edge';
    if (screen.bulge !== 0) return 'screen-edge-curved';
    if (screen.a.y !== 0 || screen.b.y !== 0) return 'screen-edge-off-wall';
    if (screen.a.x !== -screen.b.x) return 'screen-edge-asymmetric';
    if (screen.chord < OUTLINE_LIMITS.minScreenEdge) return 'screen-edge-short';
    if (area < OUTLINE_LIMITS.minArea) return 'too-small';
    const { width, depth } = symmetricSize(o);
    if (width < OUTLINE_LIMITS.roomMin || depth < OUTLINE_LIMITS.roomMin) return 'too-small';
    if (width > OUTLINE_LIMITS.roomMax || depth > OUTLINE_LIMITS.roomMax) return 'too-large';
    return null;
}

/** Whether every sampled point and segment midpoint of `inner` lies inside `outer`. */
function pathInside(outer: OutlineLike, inner: ClosedPath): boolean {
    const { points } = tessellate(inner, OUTLINE_LIMITS.validationTol);
    return points.every((p, i) => {
        const q = points[(i + 1) % points.length];
        return pointInOutline(outer, p, 5) && pointInOutline(outer, { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }, 5);
    });
}

function validateLevel(
    level: RoomGeometry['levels'][number],
    room: { height: number },
    roomShape: OutlineLike | null
): OutlineError | null {
    if (
        !isInt(level.elevation) ||
        level.elevation < OUTLINE_LIMITS.minElevation ||
        level.elevation > room.height - OUTLINE_LIMITS.headroom ||
        !isInt(level.slabThickness) ||
        level.slabThickness < OUTLINE_LIMITS.minSlab ||
        level.slabThickness > Math.min(OUTLINE_LIMITS.maxSlab, level.elevation)
    ) {
        return 'level-elevation';
    }
    const structural = validatePath(level.outline, OUTLINE_LIMITS.maxLevelVertices);
    if (structural) return structural;
    const area = signedArea(level.outline);
    if (area <= 0) return 'not-ccw';
    if (selfIntersects(tessellate(level.outline, OUTLINE_LIMITS.validationTol).points, true)) return 'self-intersect';
    if (area < OUTLINE_LIMITS.minLevelArea) return 'too-small';
    const vertexIds = new Set(level.outline.vertices.map((v) => v.id));
    if (!Array.isArray(level.frontEdges) || level.frontEdges.some((id) => !vertexIds.has(id)))
        return 'level-front-edge';
    if (roomShape && !pathInside(roomShape, level.outline)) return 'level-outside-room';
    return null;
}

export function validateGeometry(
    g: RoomGeometry,
    room: { height: number; width?: number; depth?: number }
): OutlineError | null {
    if (g.outline) {
        const error = validateOutline(g.outline);
        if (error) return error;
    }
    if (g.levels.length > OUTLINE_LIMITS.maxLevels) return 'too-many';
    const roomShape: OutlineLike | null =
        g.outline ?? (room.width && room.depth ? rectangleOutline(room.width, room.depth) : null);
    const ids = new Set<string>();
    for (const level of g.levels) {
        if (typeof level.id !== 'string' || level.id === '' || level.id === 'main' || ids.has(level.id))
            return 'bad-vertex';
        ids.add(level.id);
        const error = validateLevel(level, room, roomShape);
        if (error) return error;
    }
    return null;
}

const PROFILE_CAPS: readonly string[] = ['none', 'handrail', 'brass', 'glass'];
const PROFILE_LEDS: readonly string[] = ['none', 'top', 'base', 'face'];
const PROFILE_RISERS: readonly string[] = ['plain', 'grille', 'led'];
/** Counter cupholder spacing, mm; the server's IsObjectProfile holds the same bounds. */
export const CUPHOLDER_SPACING_RANGE = { min: 300, max: 3_000 } as const;
/** Screen bezel, mm; the server's IsObjectProfile holds the same bounds. */
export const BEZEL_RANGE = { width: { min: 20, max: 1_500 }, depth: { min: 10, max: 600 } } as const;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

function validateProfileOptions(p: ObjectProfile): OutlineError | null {
    const { steps, openings, thickness } = p;
    if (steps !== undefined) {
        const ok =
            isInt(steps.count) &&
            steps.count >= 0 &&
            steps.count <= 12 &&
            isInt(steps.tread) &&
            steps.tread >= 50 &&
            steps.tread <= 1_000;
        if (!ok) return 'bad-profile';
    }
    if (openings !== undefined) {
        const ok =
            Array.isArray(openings) &&
            openings.length <= 8 &&
            openings.every((o) => isInt(o.at) && o.at >= 0 && isInt(o.width) && o.width >= 300 && o.width <= 10_000);
        if (!ok) return 'bad-profile';
    }
    if (thickness !== undefined && !(isInt(thickness) && thickness >= 10 && thickness <= 2_000)) return 'bad-profile';
    if (p.cap !== undefined && !PROFILE_CAPS.includes(p.cap)) return 'bad-profile';
    if (p.led !== undefined && !PROFILE_LEDS.includes(p.led)) return 'bad-profile';
    if (p.riser !== undefined && !PROFILE_RISERS.includes(p.riser)) return 'bad-profile';
    const spacing = p.cupholderSpacing;
    if (
        spacing !== undefined &&
        !(isInt(spacing) && spacing >= CUPHOLDER_SPACING_RANGE.min && spacing <= CUPHOLDER_SPACING_RANGE.max)
    ) {
        return 'bad-profile';
    }
    if (p.cupholders !== undefined && typeof p.cupholders !== 'boolean') return 'bad-profile';
    if (p.color !== undefined && !HEX_COLOR.test(p.color)) return 'bad-profile';
    if (p.texture !== undefined && !(OBJECT_TEXTURES as readonly string[]).includes(p.texture)) return 'bad-profile';
    const bezel = p.bezel;
    if (
        bezel !== undefined &&
        !(
            isInt(bezel.width) &&
            bezel.width >= BEZEL_RANGE.width.min &&
            bezel.width <= BEZEL_RANGE.width.max &&
            isInt(bezel.depth) &&
            bezel.depth >= BEZEL_RANGE.depth.min &&
            bezel.depth <= BEZEL_RANGE.depth.max
        )
    ) {
        return 'bad-profile';
    }
    return null;
}

export function validateProfile(p: ObjectProfile): OutlineError | null {
    const points = p.path?.points;
    if (!Array.isArray(points)) return 'bad-profile';
    const closed = p.path.closed === true;
    if (points.length < (closed ? 3 : 2)) return 'too-few';
    if (points.length > OUTLINE_LIMITS.maxProfilePoints) return 'too-many';
    for (const pt of points) {
        if (!isInt(pt.x) || !isInt(pt.y)) return 'bad-profile';
        if (Math.abs(pt.x) > OUTLINE_LIMITS.maxAbsX || Math.abs(pt.y) > OUTLINE_LIMITS.maxAbsX) return 'too-large';
        if (!validBulge(pt.bulge)) return 'bulge-range';
    }
    const edgeCount = closed ? points.length : points.length - 1;
    const ring: PlanPoint[] = [];
    for (let i = 0; i < edgeCount; i += 1) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        if (Math.hypot(b.x - a.x, b.y - a.y) < OUTLINE_LIMITS.minProfileChord) return 'short-edge';
        ring.push(...tessellateEdge(a, b, a.bulge ?? 0, OUTLINE_LIMITS.validationTol).slice(0, -1));
    }
    if (!closed) {
        const last = points[points.length - 1];
        ring.push({ x: last.x, y: last.y });
    }
    if (selfIntersects(ring, closed)) return 'self-intersect';
    return validateProfileOptions(p);
}
